import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Preloader, useToast } from "randmarcomps";
import { CircleHelp, Sprout, Warehouse } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Product as RTKProduct, useGetV4ResellerByRouteResellerIdProductAndRandmarSkuQuery } from "randmar-api-client";
import { Product } from "../../stores/product/Product";
import AddProductToOpportunity from "./AddProductToOpportunity";
import AddToRequisition from "./AddToRequisition";
import ProductDescriptionTable from "./ProductDescriptionTable";

// Type guard to check if the product is the new RTK type
function isRTKProduct(product: Product | RTKProduct): product is RTKProduct {
  return !("source" in product);
}

interface IProps {
  defaultProduct?: Product | RTKProduct;
  randmarSKU: string;
}

const ProductDetailsModal: React.FC<IProps> = ({ defaultProduct, randmarSKU }) => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const [open, setOpen] = useState(false);
  const [skip, setSkip] = useState(true);
  const [addProductToOpportunityOpen, setAddProductToOpportunityOpen] = useState(false);
  const { toast } = useToast();

  const {
    data: rtkProduct,
    isLoading,
    isError,
  } = useGetV4ResellerByRouteResellerIdProductAndRandmarSkuQuery(
    { routeResellerId: applicationId!, randmarSku: randmarSKU, withSpecification: true },
    { skip }
  );

  const handleButtonClicked = () => {
    setSkip(false);
    setOpen(true);
  };

  if (isError) {
    toast({
      title: "Error",
      description: "Failed to load product details.",
      variant: "destructive",
    });
    return null;
  }

  // Normalize the product data to the legacy `Product` type for child components
  let productToShow: Product | undefined;
  if (rtkProduct) {
    productToShow = Product.convertFromNewProduct(rtkProduct);
  } else if (defaultProduct) {
    productToShow = isRTKProduct(defaultProduct)
      ? Product.convertFromNewProduct(defaultProduct)
      : defaultProduct;
  }


  return (
    <>
      <Button variant="ghost" size="icon" disabled={open} onClick={handleButtonClicked} title="View Inventory">
        {isLoading ? (
          <Preloader />
        ) : (
          <Warehouse className="text-primary" />
        )}
      </Button>

      {productToShow && (
        <Dialog
          open={open}
          onOpenChange={setOpen}
        >
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-center">
                {`${productToShow.manufacturerName} ${productToShow.mpn}`}
              </DialogTitle>
              <DialogDescription className="text-center">
                Detailed product information and inventory status.
              </DialogDescription>
            </DialogHeader>

            {/* OpenOrdersSummary is a MobX component, so it's removed for now */}
            {/* <OpenOrdersSummary openOrders={openOrders.filter(o => o.randmarSKU === product?.randmarSKU)} /> */}

            {isLoading ? (
              <div className="grid place-items-center p-8">
                <Preloader />
              </div>
            ) : (
              <ProductDescriptionTable product={productToShow} />
            )}

            <DialogFooter>
              {productToShow.isManufacturer && (
                <>
                  <Button variant="outline" onClick={() => setAddProductToOpportunityOpen(true)}>
                    <Sprout className="mr-2 h-4 w-4" />
                    <span>Add to Opportunity</span>
                  </Button>

                  <Button variant="outline">
                    <CircleHelp className="mr-2 h-4 w-4" />
                    <AddToRequisition product={productToShow}>
                      <span>Add to Draft Purchase</span>
                    </AddToRequisition>
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {productToShow && (
        <AddProductToOpportunity
          product={productToShow}
          open={addProductToOpportunityOpen}
          onOpenChange={setAddProductToOpportunityOpen}
        />
      )}
    </>
  );
};

export default ProductDetailsModal;
